import React, { useState, useCallback, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/dashboard/DashboardLayout';
import ClassScheduleCalendar from '../../components/ClassScheduleCalendar';
import { 
  Calendar, 
  Users, 
  DollarSign, 
  Clock, 
  Video, 
  TrendingUp,
  Plus,
  Edit,
  Trash2,
  FileText,
  CheckCircle,
  MessageSquare,
  Send,
  User,
  Target,
  Award,
  BookOpen
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { mockClasses, mockStudents } from '../../mockData/dashboardData';
import { mockTasks, mockTimesheets, getTutorTasks, getTutorTimesheets, getTutorMonthlyPayout } from '../../mockData/taskData';
import { LineChart, Line, BarChart as RechartsBarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import AddTimesheetModal from '../../components/modals/AddTimesheetModal';
import ProfileView from '../../components/ProfileView';
import TutorAvailabilityScheduler from '../../components/TutorAvailabilityScheduler';
import TutorEffectiveAvailabilityView from '../../components/TutorEffectiveAvailabilityView';
import MessagesPage from './MessagesPage';

// ============================================
// EXTRACTED COMPONENT - MessagesView (outside main component to prevent re-creation)
// ============================================
const MessagesView = React.memo(({ messageText, handleMessageTextChange }) => {
  const mockMessages = [
    {
      id: 1,
      from: 'Mrs. Kumar',
      role: 'Parent',
      message: 'Thank you for the excellent teaching!',
      timestamp: '1 hour ago',
      unread: true
    },
    {
      id: 2,
      from: 'Admin Team',
      role: 'Admin',
      message: 'Your payout has been processed.',
      timestamp: '1 day ago',
      unread: false
    }
  ];

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-1 bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Messages</h2>
        <div className="space-y-3">
          {mockMessages.map((msg) => (
            <div
              key={msg.id}
              className={`border rounded-xl p-4 cursor-pointer hover:border-cyan-300 transition-colors ${
                msg.unread ? 'border-cyan-200 bg-cyan-50 dark:bg-gray-700' : 'border-gray-200'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-cyan-100 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-cyan-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 dark:text-white text-sm">{msg.from}</h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{msg.role}</p>
                  <p className="text-sm text-gray-700 dark:text-gray-300">{msg.message}</p>
                  <p className="text-xs text-gray-500 mt-1">{msg.timestamp}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-2xl shadow-lg flex flex-col h-[600px]">
        <div className="border-b border-gray-200 dark:border-gray-700 p-6">
          <h3 className="font-bold text-gray-900 dark:text-white">Mrs. Kumar</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">Parent</p>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-cyan-100 rounded-full flex items-center justify-center">
              <User className="w-4 h-4 text-cyan-600" />
            </div>
            <div>
              <div className="bg-gray-100 dark:bg-gray-700 rounded-2xl p-4">
                <p className="text-sm text-gray-900 dark:text-white">Thank you for the excellent teaching!</p>
              </div>
              <p className="text-xs text-gray-500 mt-1">1 hour ago</p>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-3">
            <Input
              placeholder="Type your message..."
              value={messageText}
              onChange={handleMessageTextChange}
              className="flex-1"
            />
            <Button className="bg-cyan-600 hover:bg-cyan-700 text-white">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
});

const TutorDashboard = () => {
  const { user, getAccessToken } = useAuth();
  const [activeView, setActiveView] = useState('dashboard');
  const [timesheetModalOpen, setTimesheetModalOpen] = useState(false);
  const [timesheets, setTimesheets] = useState([]);
  const [editingTimesheet, setEditingTimesheet] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [tasks, setTasks] = useState([]);
  const [schedules, setSchedules] = useState([]);  // Add schedules state
  const [students, setStudents] = useState([]);  // Add students state
  const studentsFetched = useRef(false);  // Track if students have been fetched
  const [scheduleViewMode, setScheduleViewMode] = useState('class-schedule'); // 'class-schedule' or 'weekly-availability'
  
  const tutorId = 1;
  const myTasks = getTutorTasks(tutorId);
  const myClasses = mockClasses.filter(c => c.tutor.includes('Priya'));

  // Fetch tasks, timesheets, and schedules from backend
  useEffect(() => {
    fetchTasks();
    fetchTimesheets();
    fetchSchedules();
  }, []);

  // Fetch students after tasks are loaded
  useEffect(() => {
    if (tasks.length > 0 && !studentsFetched.current) {
      console.log('Tasks loaded, fetching students...');
      studentsFetched.current = true;
      fetchStudents();
    }
  }, [tasks]);

  const fetchTasks = async () => {
    try {
      const token = getAccessToken();
      if (!token) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/schedules/tasks`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const backendTasks = await response.json();
        console.log('Fetched tasks:', backendTasks);
        setTasks(backendTasks);
      } else {
        console.error('Failed to fetch tasks:', await response.text());
      }
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    }
  };

  const fetchTimesheets = async () => {
    try {
      const token = getAccessToken();
      if (!token) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/timesheets?page=1&page_size=1000`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('Fetched timesheets:', data);
        // Handle both old and new response formats
        const backendTimesheets = data.timesheets || data;
        setTimesheets(Array.isArray(backendTimesheets) ? backendTimesheets : []);
      }
    } catch (error) {
      console.error('Failed to fetch timesheets:', error);
    }
  };

  const fetchSchedules = async () => {
    try {
      const token = getAccessToken();
      if (!token) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/schedules`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const backendSchedules = await response.json();
        console.log('Fetched schedules:', backendSchedules);
        setSchedules(backendSchedules);
      }
    } catch (error) {
      console.error('Failed to fetch schedules:', error);
    }
  };

  const fetchStudents = async () => {
    try {
      const token = getAccessToken();
      if (!token || tasks.length === 0) {
        console.log('Cannot fetch students - token or tasks missing', { token: !!token, tasksLength: tasks.length });
        return;
      }

      // Get unique student IDs from tasks
      const studentIds = [...new Set(tasks.map(t => t.student_id))];
      console.log('📚 Fetching students for IDs:', studentIds);
      console.log('📋 Total tasks:', tasks.length);

      // Fetch all users and filter for our students
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/users`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const allUsers = await response.json();
        console.log('👥 All users fetched:', allUsers.length);
        
        // Filter to only include students assigned to this tutor
        const myStudents = allUsers.filter(user => 
          studentIds.includes(user.id) && user.role === 'student'
        );
        console.log('✅ My students after filtering:', myStudents);
        console.log('📊 Students found:', myStudents.map(s => ({ id: s.id, name: s.full_name })));
        setStudents(myStudents);
      } else {
        console.error('Failed to fetch users:', response.status);
      }
    } catch (error) {
      console.error('Failed to fetch students:', error);
    }
  };

  // Calculate stats from real data
  const myTimesheets = timesheets;
  
  // Get current month timesheets
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();
  
  const currentMonthTimesheets = timesheets.filter(t => {
    const timesheetDate = new Date(t.work_date);
    return timesheetDate.getMonth() === currentMonth && timesheetDate.getFullYear() === currentYear;
  });
  
  const totalHoursThisMonth = currentMonthTimesheets.reduce((sum, t) => sum + t.hours_worked, 0);
  
  const monthlyPayout = currentMonthTimesheets.reduce((sum, t) => {
    const task = tasks.find(task => task.id === t.task_id);
    
    // Step 1: Calculate base payout (group class or regular rate)
    const rate = (t.is_group_class && task?.group_class_payout_rate) 
      ? task.group_class_payout_rate 
      : (task?.payout_rate || 0);
    let payoutAmount = t.hours_worked * rate;
    
    // Step 2: Add odd hour payout if applicable
    if (t.is_odd_hour && task?.odd_hour_payout) {
      payoutAmount += parseFloat(task.odd_hour_payout);
    }
    
    return sum + payoutAmount;
  }, 0);
  
  // Get unique students from tasks
  const myStudents = tasks.map(task => {
    const studentDetails = students.find(s => s.id === task.student_id);
    return {
      id: task.student_id,
      name: studentDetails?.full_name || 'Student',
      subject: task.subject,
      taskId: task.id,
      sessionsThisMonth: currentMonthTimesheets.filter(ts => ts.task_id === task.id).length
    };
  });
  
  // Calculate earnings data for last 6 months
  const earningsData = [];
  for (let i = 5; i >= 0; i--) {
    const date = new Date(currentYear, currentMonth - i, 1);
    const month = date.toLocaleString('default', { month: 'short' });
    const monthTimesheets = timesheets.filter(t => {
      const tsDate = new Date(t.work_date);
      return tsDate.getMonth() === date.getMonth() && tsDate.getFullYear() === date.getFullYear();
    });
    
    const earnings = monthTimesheets.reduce((sum, t) => {
      const task = tasks.find(task => task.id === t.task_id);
      
      // Step 1: Calculate base payout (group class or regular rate)
      const rate = (t.is_group_class && task?.group_class_payout_rate) 
        ? task.group_class_payout_rate 
        : (task?.payout_rate || 0);
      let payoutAmount = t.hours_worked * rate;
      
      // Step 2: Add odd hour payout if applicable
      if (t.is_odd_hour && task?.odd_hour_payout) {
        payoutAmount += parseFloat(task.odd_hour_payout);
      }
      
      return sum + payoutAmount;
    }, 0);
    
    earningsData.push({ month, earnings });
  }

  // Stable callback handler to prevent input re-mounting
  const handleMessageTextChange = useCallback((e) => {
    setMessageText(e.target.value);
  }, []);

  const stats = [
    { 
      label: 'Sessions This Month', 
      value: currentMonthTimesheets.length, 
      icon: Calendar, 
      color: 'from-blue-400 to-cyan-500' 
    },
    { 
      label: 'Active Tasks', 
      value: tasks.filter(t => t.status === 'active').length, 
      icon: Users, 
      color: 'from-green-400 to-emerald-500' 
    },
    { 
      label: 'This Month Payout', 
      value: `₹${Math.round(monthlyPayout).toLocaleString()}`, 
      icon: DollarSign, 
      color: 'from-yellow-400 to-orange-500' 
    },
    { 
      label: 'Timesheets This Month', 
      value: currentMonthTimesheets.length, 
      icon: FileText, 
      color: 'from-purple-400 to-pink-500' 
    }
  ];

  const mockMessages = [
    {
      id: 1,
      from: 'Mrs. Kumar',
      role: 'Parent',
      message: 'Thank you for the extra attention you gave to Rahul this week.',
      timestamp: '2 hours ago',
      unread: true
    },
    {
      id: 2,
      from: 'Ananya Patel',
      role: 'Student',
      message: 'Can you explain the last physics problem again?',
      timestamp: '1 day ago',
      unread: false
    }
  ];

  const handleTimesheetSubmit = (timesheetData) => {
    // Refresh timesheets from backend
    fetchTimesheets();
    setTimesheetModalOpen(false);
    setEditingTimesheet(null);
  };

  const handleDeleteTimesheet = async (timesheetId) => {
    if (!window.confirm('Are you sure you want to delete this timesheet?')) return;
    
    try {
      const token = getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/timesheets/${timesheetId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.ok) {
        fetchTimesheets();
        alert('Timesheet deleted successfully!');
      } else {
        const error = await response.json();
        alert(`Failed to delete timesheet: ${error.detail}`);
      }
    } catch (error) {
      console.error('Failed to delete timesheet:', error);
      alert('Failed to delete timesheet');
    }
  };

  const handleEditTimesheet = (timesheet) => {
    setEditingTimesheet(timesheet);
    setTimesheetModalOpen(true);
  };

  // Dashboard Overview
  const DashboardOverview = () => {
    // Filter today's classes - need to account for timezone conversion
    const today = new Date();
    // Get today's date in user's local timezone
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    
    const todaysClasses = schedules.filter(schedule => {
      // The backend stores dates in UTC, but we need to compare with user's local date
      // Parse the UTC date and time
      const scheduleDateTime = new Date(`${schedule.scheduled_date}T${schedule.scheduled_time}Z`);
      // Convert to local date string
      const scheduleDateStr = `${scheduleDateTime.getFullYear()}-${String(scheduleDateTime.getMonth() + 1).padStart(2, '0')}-${String(scheduleDateTime.getDate()).padStart(2, '0')}`;
      return scheduleDateStr === todayStr;
    });
    
    return (
      <div className="grid lg:grid-cols-2 gap-6">
        {/* My Assigned Tasks */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
            <Users className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
            My Assigned Tasks ({tasks.filter(t => t.status === 'active').length})
          </h2>
          <div className="space-y-4">
            {tasks.filter(t => t.status === 'active').length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <BookOpen className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No tasks assigned yet</p>
              </div>
            ) : (
              tasks.filter(t => t.status === 'active').map((task) => (
                <div key={task.id} className="border-2 border-cyan-200 dark:border-cyan-800 bg-cyan-50 dark:bg-gray-700 rounded-xl p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-bold text-gray-900 dark:text-white text-lg">{task.title}</h3>
                      <p className="text-sm text-cyan-600 dark:text-cyan-400 mt-1">{task.subject}</p>
                      {task.description && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{task.description}</p>
                      )}
                    </div>
                    <span className="px-3 py-1 bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 text-xs font-semibold rounded-full">
                      Active
                    </span>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-3 mt-3">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Your Rate</p>
                    <p className="text-2xl font-bold text-green-600">₹{task.payout_rate || 0}/hour</p>
                    {task.start_date && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Started: {new Date(task.start_date).toLocaleDateString()}</p>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Today's Classes */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Today's Classes</h2>
          <div className="space-y-4">
            {todaysClasses.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <Calendar className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No classes scheduled for today</p>
              </div>
            ) : (
              todaysClasses.map((schedule) => {
                const task = tasks.find(t => t.id === schedule.task_id);
                const startTime = new Date(`${schedule.scheduled_date}T${schedule.scheduled_time}Z`);
                
                return (
                  <div key={schedule.id} className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-bold text-gray-900 dark:text-white">{task?.subject || 'Class'}</h3>
                      <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">Scheduled</span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Task: {task?.title || 'Unknown'}</p>
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-3">
                      <Clock className="w-4 h-4" />
                      <span>{startTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })} ({schedule.duration_minutes} min)</span>
                    </div>
                    {schedule.meeting_link ? (
                      <Button 
                        size="sm" 
                        className="w-full bg-cyan-600 hover:bg-cyan-700 text-white"
                        onClick={() => window.open(schedule.meeting_link, '_blank')}
                      >
                        <Video className="w-4 h-4 mr-2" />
                        Join Google Meet
                      </Button>
                    ) : (
                      <Button 
                        size="sm" 
                        className="w-full bg-gray-400 text-white cursor-not-allowed"
                        disabled
                      >
                        <Video className="w-4 h-4 mr-2" />
                        No Meet Link
                      </Button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    );
  };

  // My Schedule View - Now with Calendar, Availability, and Available Slots tabs
  const MyScheduleView = () => (
    <div className="space-y-6">
      {/* View Mode Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-lg">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setScheduleViewMode('class-schedule')}
            className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
              scheduleViewMode === 'class-schedule'
                ? 'bg-cyan-600 text-white shadow-md'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <Calendar className="w-4 h-4" />
            Class Schedule
          </button>
          <button
            onClick={() => setScheduleViewMode('weekly-availability')}
            className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
              scheduleViewMode === 'weekly-availability'
                ? 'bg-cyan-600 text-white shadow-md'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <Clock className="w-4 h-4" />
            Set Weekly Availability
          </button>
          <button
            onClick={() => setScheduleViewMode('available-slots')}
            className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
              scheduleViewMode === 'available-slots'
                ? 'bg-green-600 text-white shadow-md'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <CheckCircle className="w-4 h-4" />
            My Available Slots
          </button>
        </div>
      </div>

      {/* Class Schedule View */}
      {scheduleViewMode === 'class-schedule' && (
        tasks.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg text-center">
            <BookOpen className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500 dark:text-gray-400 mb-4">No tasks assigned yet. Please contact admin to assign tasks.</p>
          </div>
        ) : (
          <ClassScheduleCalendar tasks={tasks} onScheduleChange={fetchSchedules} />
        )
      )}

      {/* Weekly Availability View - Edit Mode */}
      {scheduleViewMode === 'weekly-availability' && (
        <TutorAvailabilityScheduler readOnly={false} />
      )}

      {/* My Available Slots View - Shows effective availability excluding scheduled classes */}
      {scheduleViewMode === 'available-slots' && (
        <TutorEffectiveAvailabilityView title="My Available Slots" />
      )}
    </div>
  );

  // My Timesheet View - Separated from Schedule
  const MyTimesheetView = () => (
    <div className="space-y-6">
      {/* Timesheet Management */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <FileText className="w-6 h-6 text-purple-600" />
            My Timesheets
          </h2>
          <Button 
            onClick={() => setTimesheetModalOpen(true)}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Timesheet
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Date</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Student</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Subject</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-900 dark:text-white">Duration</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-900 dark:text-white">My Payout</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900 dark:text-white">Status</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900 dark:text-white">Actions</th>
              </tr>
            </thead>
            <tbody>
              {myTimesheets.length === 0 ? (
                <tr>
                  <td colSpan="7" className="py-12 text-center text-gray-500 dark:text-gray-400">
                    <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p>No timesheets yet. Create your first timesheet!</p>
                  </td>
                </tr>
              ) : (
                myTimesheets.map((timesheet) => {
                  const task = tasks.find(t => t.id === timesheet.task_id);
                  
                  // Step 1: Calculate base payout (group class or regular rate)
                  const rate = (timesheet.is_group_class && task?.group_class_payout_rate) 
                    ? task.group_class_payout_rate 
                    : (task?.payout_rate || 0);
                  let payoutAmount = timesheet.hours_worked * rate;
                  
                  // Step 2: Add odd hour payout if applicable
                  if (timesheet.is_odd_hour && task?.odd_hour_payout) {
                    payoutAmount += parseFloat(task.odd_hour_payout);
                  }
                  
                  return (
                    <tr key={timesheet.id} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="py-3 px-4">{new Date(timesheet.work_date).toLocaleDateString()}</td>
                      <td className="py-3 px-4">{task ? task.title : 'Unknown Task'}</td>
                      <td className="py-3 px-4">{task ? task.subject : '-'}</td>
                      <td className="py-3 px-4 text-right">{timesheet.hours_worked.toFixed(1)}h</td>
                      <td className="py-3 px-4 text-right font-bold text-green-600">₹{payoutAmount.toFixed(2)}</td>
                      <td className="py-3 px-4 text-center">
                        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                          timesheet.status === 'submitted' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' : 
                          'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                          {timesheet.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          {timesheet.status === 'submitted' && (
                            <>
                              <button 
                                onClick={() => handleEditTimesheet(timesheet)}
                                className="p-1 hover:bg-blue-50 dark:hover:bg-blue-900 rounded"
                                title="Edit timesheet"
                              >
                                <Edit className="w-4 h-4 text-blue-600" />
                              </button>
                              <button 
                                onClick={() => handleDeleteTimesheet(timesheet.id)}
                                className="p-1 hover:bg-red-50 dark:hover:bg-red-900 rounded"
                                title="Delete timesheet"
                              >
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // Students View
  const StudentsView = () => {
    // Get unique students from tasks
    console.log('🔍 All tasks with student IDs:', tasks.map(t => ({ 
      taskId: t.id, 
      taskTitle: t.title,
      studentId: t.student_id 
    })));
    
    const uniqueStudentIds = Array.from(new Set(tasks.map(t => t.student_id)));
    console.log('Unique student IDs:', uniqueStudentIds);
    console.log('Students array:', students);
    console.log('Tasks:', tasks);
    
    const uniqueStudents = uniqueStudentIds.map(studentId => {
        const studentTasks = tasks.filter(t => t.student_id === studentId);
        const sessionsCount = timesheets.filter(ts => 
          studentTasks.some(task => task.id === ts.task_id)
        ).length;
        
        // Find student details from students array
        const studentDetails = students.find(s => s.id === studentId);
        console.log(`Looking for student ${studentId}, found:`, studentDetails);
        
        return {
          id: studentId,
          name: studentDetails?.full_name || 'Student',
          email: studentDetails?.email || '',
          tasks: studentTasks,
          sessionsCount,
          subjects: [...new Set(studentTasks.map(t => t.subject))]
        };
      });
    
    console.log('Unique students:', uniqueStudents);

    return (
      <div className="space-y-6">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
            <Users className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
            My Students ({uniqueStudents.length})
          </h2>
          {uniqueStudents.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p>No students assigned yet</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {uniqueStudents.map((student) => (
                <div key={student.id} className="border border-gray-200 dark:border-gray-700 rounded-xl p-6 hover:border-cyan-300 dark:hover:border-cyan-600 transition-colors">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center text-white text-2xl font-bold">
                      {student.name?.charAt(0) || 'S'}
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 dark:text-white">{student.name}</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{student.email}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Subjects:</p>
                      <div className="flex flex-wrap gap-2">
                        {student.subjects.map((subject, idx) => (
                          <span key={idx} className="px-2 py-1 bg-cyan-100 text-cyan-700 dark:bg-cyan-900 dark:text-cyan-300 text-xs font-medium rounded-full">
                            {subject}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Sessions Completed</span>
                        <span className="text-lg font-bold text-cyan-600 dark:text-cyan-400">{student.sessionsCount}</span>
                      </div>
                    </div>

                    <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Active Tasks</span>
                        <span className="text-lg font-bold text-green-600">{student.tasks.length}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      {/* Performance Analytics */}
      <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl p-6 shadow-lg text-white">
        <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Target className="w-6 h-6" />
          Student Performance Insights
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white/10 rounded-lg p-4">
            <p className="text-sm font-semibold mb-1">🌟 Top Performer</p>
            <p className="text-sm text-purple-100">Ananya Patel - 85% average score</p>
          </div>
          <div className="bg-white/10 rounded-lg p-4">
            <p className="text-sm font-semibold mb-1">💡 Needs Attention</p>
            <p className="text-sm text-purple-100">Rahul Kumar - Focus on problem-solving</p>
          </div>
        </div>
      </div>
    </div>
    );
  };

  // Earnings View
  const EarningsView = () => {
    const [payouts, setPayouts] = useState([]);
    const [payoutsLoading, setPayoutsLoading] = useState(false);

    // Fetch payouts when the component mounts
    useEffect(() => {
      fetchPayouts();
    }, []);

    const fetchPayouts = async () => {
      try {
        setPayoutsLoading(true);
        const token = getAccessToken();
        if (!token) return;

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/payouts?page=1&limit=100`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          console.log('Fetched payouts:', data);
          setPayouts(data.payouts || []);
        } else {
          console.error('Failed to fetch payouts:', await response.text());
        }
      } catch (error) {
        console.error('Failed to fetch payouts:', error);
      } finally {
        setPayoutsLoading(false);
      }
    };

    return (
      <div className="space-y-6">
        {/* Earnings Summary */}
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-6 text-white shadow-lg">
            <h3 className="text-sm font-medium text-green-100 mb-2">This Month Earnings</h3>
            <p className="text-3xl font-bold">₹{Math.round(monthlyPayout).toLocaleString()}</p>
            <p className="text-sm text-green-100 mt-2">{currentMonthTimesheets.length} sessions completed</p>
          </div>
          <div className="bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl p-6 text-white shadow-lg">
            <h3 className="text-sm font-medium text-blue-100 mb-2">Total Hours</h3>
            <p className="text-3xl font-bold">{totalHoursThisMonth.toFixed(1)}h</p>
            <p className="text-sm text-blue-100 mt-2">This month</p>
          </div>
          <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl p-6 text-white shadow-lg">
            <h3 className="text-sm font-medium text-purple-100 mb-2">Timesheets</h3>
            <p className="text-3xl font-bold">{currentMonthTimesheets.length}</p>
            <p className="text-sm text-purple-100 mt-2">Submitted this month</p>
          </div>
        </div>

        {/* Earnings Charts - Side by Side */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Earnings Trend Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-green-600" />
              Earnings Trend
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={earningsData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip />
                <Line type="monotone" dataKey="earnings" stroke="#10b981" strokeWidth={3} dot={{ fill: '#10b981', r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Monthly Breakdown Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Monthly Breakdown</h2>
            <ResponsiveContainer width="100%" height={300}>
              <RechartsBarChart data={earningsData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip />
                <Bar dataKey="earnings" fill="#10b981" radius={[8, 8, 0, 0]} />
              </RechartsBarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Generated Payouts Section */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-green-600" />
            My Payouts
          </h2>
          
          {payoutsLoading ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <p>Loading payouts...</p>
            </div>
          ) : payouts.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <DollarSign className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p>No payouts generated yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Payout Number</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Period</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-900 dark:text-white">Amount</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-900 dark:text-white">Adjustments</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-900 dark:text-white">Deductions</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-900 dark:text-white">Final Amount</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-900 dark:text-white">Status</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {payouts.map((payout) => (
                    <tr key={payout.id} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="py-3 px-4 font-medium text-gray-900 dark:text-white">{payout.payout_number}</td>
                      <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                        {new Date(payout.period_start).toLocaleDateString()} - {new Date(payout.period_end).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4 text-right font-semibold text-gray-900 dark:text-white">
                        ₹{payout.amount.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right text-green-600">
                        {payout.adjustments > 0 ? `+₹${payout.adjustments.toLocaleString()}` : '-'}
                      </td>
                      <td className="py-3 px-4 text-right text-red-600">
                        {payout.deductions > 0 ? `-₹${payout.deductions.toLocaleString()}` : '-'}
                      </td>
                      <td className="py-3 px-4 text-right font-bold text-green-600">
                        ₹{payout.final_amount.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                          payout.status === 'paid' ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' : 
                          payout.status === 'pending' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300' :
                          payout.status === 'processing' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' :
                          'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                          {payout.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                        {new Date(payout.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout role="tutor" activeView={activeView} onViewChange={setActiveView}>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {activeView === 'dashboard' && 'Tutor Dashboard'}
            {activeView === 'schedule' && 'My Schedule'}
            {activeView === 'timesheet' && 'My Timesheets'}
            {activeView === 'students' && 'My Students'}
            {activeView === 'earnings' && 'Earnings'}
            {activeView === 'messages' && 'Messages'}
            {activeView === 'profile' && 'My Profile'}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {activeView === 'dashboard' && 'Manage your classes and track earnings'}
            {activeView === 'schedule' && 'View and manage your class schedule'}
            {activeView === 'timesheet' && 'Track and submit your timesheets'}
            {activeView === 'students' && 'Track student progress and performance'}
            {activeView === 'earnings' && 'Monitor your earnings and payout history'}
            {activeView === 'messages' && 'Communicate with students and parents'}
            {activeView === 'profile' && 'Manage your profile information'}
          </p>
        </div>

        {/* Stats - Show only on dashboard */}
        {activeView === 'dashboard' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div 
                  key={index} 
                  initial={{ opacity: 0, y: 20 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  transition={{ delay: index * 0.1 }} 
                  className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg"
                >
                  <div className={`w-12 h-12 bg-gradient-to-br ${stat.color} rounded-xl flex items-center justify-center mb-4`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">{stat.value}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{stat.label}</p>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Dynamic Content Based on Active View */}
        {activeView === 'dashboard' && <DashboardOverview />}
        {activeView === 'schedule' && <MyScheduleView />}
        {activeView === 'timesheet' && <MyTimesheetView />}
        {activeView === 'students' && <StudentsView />}
        {activeView === 'earnings' && <EarningsView />}
        {activeView === 'messages' && <MessagesPage />}
        {activeView === 'profile' && <ProfileView />}
      </div>

      {/* Add Timesheet Modal */}
      <AddTimesheetModal
        isOpen={timesheetModalOpen}
        onClose={() => {
          setTimesheetModalOpen(false);
          setEditingTimesheet(null);
        }}
        onSubmit={handleTimesheetSubmit}
        tasks={tasks}
      />
    </DashboardLayout>
  );
};

export default TutorDashboard;
